/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2023
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { useContext, useState } from 'react';
import { IValidationMessageDescriptor } from '../../shared/Utils/EquipmentSpaceMappingAndProperties/FileReaderUtils/FileValidator';
import { readOnboardingDataFromExcelFile } from '../../shared/Utils/EquipmentSpaceMappingAndProperties/FileReaderUtils/FileReader';
import {
  IAssociationDataValidations,
  useValidatorForAssociationAndConfig,
  validateInitialContents,
  validateSchema
} from '../../shared/Utils/EquipmentSpaceMappingAndProperties/DataUtils/DataValidator';
import { convertDataToAssociationSchema } from '../../shared/Utils/EquipmentSpaceMappingAndProperties/DataUtils/DataConverter';
import { convertAssociationSchemaToZLOOnboardingConfig } from './DataConverter';
import descriptors from '../../../locales/en-US/descriptors';
import _ from 'lodash';
import { ApiContext } from '../../../context/ApiContext';
import { getZLONotifyInfo } from '../../shared/Utils/UtilitySettings/UtilitySettingsUtils';
import { useTranslation } from '@scuf/localization';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';

const d = descriptors.energyManagement;
const z = descriptors.ZLOOnboardingConfig;

export enum IActionProgressStatus {
  started = 'started',
  completed = 'completed',
  error = 'error'
}

/**
 * Hook to call validation flow
 *
 * @param requiredZLOSubHeaders Required ZLO SubHeaders for the Template
 * @param getSiteDataFn Function to retrieve site Data
 * @returns valStatus, allValid, validationStepMsg, errorMsgs, and validateUpload function
 */
export const useZLODataValidator = (requiredZLOSubHeaders: IAny[]) => {
  const [valStatus, setValStatus] = useState<IActionProgressStatus>();
  const [allValid, setAllValid] = useState<boolean>(false);
  const [numErrors, setNumErrors] = useState<number>(0);
  const [validationStepMsg, setValidationStepMsg] = useState<
    IValidationMessageDescriptor
  >();
  const [errorMsgs, setErrorMsgs] = useState<IValidationMessageDescriptor[]>(
    []
  );

  // - Data Validation Core Logic Hook
  const {
    validateAssocData: assocDataValidatorFn
  } = useValidatorForAssociationAndConfig();

  const apiContext = useContext(ApiContext);
  const { getSiteDataForAssociationValidation } = apiContext;

  const _updateErrorMsgs = (m: IValidationMessageDescriptor[]) => {
    setErrorMsgs((o) => [...o, ...m]);
  };

  const validateUpload = async (file: File) => {
    _clearState();
    setValStatus(IActionProgressStatus.started);
    try {
      setValidationStepMsg({ d: z.VALIDATING_FILE_SCHEMA });
      const excelData = await readOnboardingDataFromExcelFile(file);

      const schemaValidation = validateSchema(excelData, requiredZLOSubHeaders);

      if (schemaValidation.success) {
        const isInitialContentValid = validateInitialContents(excelData);
        if (isInitialContentValid.success) {
          const res = convertDataToAssociationSchema(excelData);
          const zloData = convertAssociationSchemaToZLOOnboardingConfig(
            res.data
          );
          if (getSiteDataForAssociationValidation) {
            try {
              setValidationStepMsg({ d: z.VALIDATING_DATA_ENTRIES });
              const siteData = await getSiteDataForAssociationValidation();

              const {
                allValid: _allValid,
                validatedData,
                numErrors: valErrors
              } = assocDataValidatorFn(
                zloData,
                siteData.allSpatialElements,
                siteData.allElements
              );
              setValStatus(IActionProgressStatus.completed);
              setAllValid(_allValid);
              setNumErrors(valErrors);
              return validatedData;
            } catch (apiErr) {
              setValStatus(IActionProgressStatus.completed);
              _updateErrorMsgs([
                {
                  d: z.SOME_ERROR_FETCHING_DATA,
                  args: { errorString: _.toString(apiErr) }
                }
              ]);
              return;
            }
          } else {
            setValStatus(IActionProgressStatus.completed);
            _updateErrorMsgs([
              {
                d: z.SOME_ERROR_FETCHING_DATA,
                args: { errorString: 'Get Data Function undefined' }
              }
            ]);
            return;
          }
        } else {
          setValStatus(IActionProgressStatus.completed);
          _updateErrorMsgs(isInitialContentValid.messages);
          return;
        }
      } else {
        setValStatus(IActionProgressStatus.completed);
        _updateErrorMsgs(schemaValidation.messages);
        return;
      }
    } catch (e) {
      setValidationStepMsg({
        d: descriptors.equipmentSpaceMapping.SOME_ERROR_GENERAL,
        args: { errorString: _.toString(e) }
      });
      _updateErrorMsgs([
        {
          d: descriptors.equipmentSpaceMapping.SOME_ERROR_GENERAL,
          args: { errorString: _.toString(e) }
        }
      ]);
    }
  };

  const _clearState = () => {
    setErrorMsgs([]);
    setAllValid(false);
    setNumErrors(0);
  };

  return {
    validationStepMsg,
    validateUpload,
    valStatus,
    allValid,
    errorMsgs,
    numErrors
  };
};

/**
 * #TODO
 * Add Submission Functions as params
 * @returns Status?
 */
export const useZLODataSubmitter = () => {
  const apiContext = useContext(ApiContext);
  const [t] = useTranslation();
  const {
    createBulkSpatialElements,
    handleAssociation,
    updatePropertiesOfElements,
    notifyMessage
  } = apiContext;
  const [submissionStatus, setSubmissionStatus] = useState<
    IActionProgressStatus
  >();
  const [submissionErrorMsg, setSubmissionErrorMsg] = useState<
    IValidationMessageDescriptor
  >({ d: descriptors.equipmentSpaceMapping.EMPTY });

  const creationAndAssociation = async (_validatedData: IAny) => {
    // Create new Elements
    const updatedValidatedData = await createSpatialElements(_validatedData);
    const associationData = updatedValidatedData?.map((sp) => {
      return {
        spatialElementId: sp.SpatialElementId,
        elements: sp.validatedAssociations?.map((el) => {
          return { elementId: el.ElementId };
        })
      };
    });
    // Association
    if (handleAssociation) {
      const res = await handleAssociation(associationData);
      if (!res) {
        throw res;
      }
    } else {
      throw new Error('Association Function undefined');
    }
  };

  const propertiesUpdate = async (_validatedData: IAny) => {
    const res = await updateElementProperties(_validatedData);
    if (!res) {
      throw res;
    }
  };

  const submitData = async (validatedData: IAssociationDataValidations[]) => {
    _clearState();
    setSubmissionStatus(IActionProgressStatus.started);
    try {
      await Promise.all([
        await creationAndAssociation(validatedData),
        await propertiesUpdate(validatedData)
      ]);
      setSubmissionStatus(IActionProgressStatus.completed);
      const { notifyDescription, notifyType } = getZLONotifyInfo();
      if (notifyMessage) {
        notifyMessage(t(notifyDescription), notifyType);
      }
    } catch (e) {
      setSubmissionStatus(IActionProgressStatus.error);
      // Change message based on which api call failed
      setSubmissionErrorMsg({ d: z.SUBMISSION_ERROR_MESSAGE });
    }
  };

  interface ICommonSpatialElement {
    id: string;
    name: string;
    validationError?: string;
  }
  interface ICreatedSpatialElement {
    spatialElement: ICommonSpatialElement;
  }
  interface ISpatialElementsResponse {
    spatialElementSuccess?: ICreatedSpatialElement[];
    spatialElementFailure?: ICommonSpatialElement[];
    id?: string;
  }

  const createSpatialElements = async (
    spatialData: IAssociationDataValidations[]
  ) => {
    const spatialElementsToBeCreated = spatialData
      .filter((item: IAssociationDataValidations) =>
        _.isEmpty(item.SpatialElementId)
      )
      .map((item: IAssociationDataValidations) => ({
        name: item.SpatialElementName
      }));
    if (!spatialElementsToBeCreated.length) {
      return spatialData;
    }
    if (createBulkSpatialElements) {
      const response: ISpatialElementsResponse = await createBulkSpatialElements(
        spatialElementsToBeCreated
      );
      let mergedSpatialData = spatialData;
      if (
        response.spatialElementSuccess &&
        response.spatialElementSuccess.length
      ) {
        mergedSpatialData = mergeSpatialData(
          response.spatialElementSuccess,
          mergedSpatialData
        );
      }

      if (
        response.spatialElementFailure &&
        response.spatialElementFailure.length
      ) {
        mergedSpatialData = mergeSpatialData(
          response.spatialElementFailure.map((sf) => ({ spatialElement: sf })),
          mergedSpatialData
        );
      }
      return mergedSpatialData;
    } else {
      throw new Error('Create Bulk Function undefined');
    }
  };

  const mergeSpatialData = (
    a: ICreatedSpatialElement[],
    b: IAssociationDataValidations[]
  ): IAssociationDataValidations[] => {
    // return a.map((itm: ICreatedSpatialElement) => ({
    //   ...b.find(
    //     (item: IAssociationDataValidations) =>
    //       item.SpatialElementName === itm.spatialElement.name && item
    //   ),
    //   ...{ SpatialElementId: itm.spatialElement.id }
    // }));
    return b.map((item) => {
      if (_.isEmpty(item.SpatialElementId)) {
        const itm = a.find(
          (_i) => _i.spatialElement.name === item.SpatialElementName
        );
        if (itm) {
          item.SpatialElementId = itm.spatialElement.id;
        }
      }
      return item;
    });
  };
  const _clearState = () => {
    setSubmissionErrorMsg({ d: descriptors.equipmentSpaceMapping.EMPTY });
  };
  interface IProperty {
    id: string;
    propertyValue: string;
  }

  interface IBulkPropertyUpdate {
    properties: IProperty[];
  }

  const updateElementProperties = async (validatedData: IAny[] | undefined) => {
    let properties: IProperty[] = [];
    if (validatedData) {
      validatedData.forEach((spatialElement) => {
        spatialElement.validatedAssociations.forEach((element: IAny) => {
          const property = element.validatedProperties.map(
            (item: IProperty) => ({
              id: item.id,
              propertyValue: item.propertyValue
            })
          );
          properties = properties.concat(property);
        });
      });
    }

    if (properties.length === 0) {
      return true;
    }

    if (updatePropertiesOfElements) {
      const reqPayload: IBulkPropertyUpdate = {
        properties
      };
      return await updatePropertiesOfElements(reqPayload);
    } else {
      throw new Error('Update Properties Function undefined');
    }
  };

  return {
    submissionStatus,
    submissionErrorMsg,
    submitData
  };
};
